import { Link, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { LogOut, Camera, Bell, History, UserCog, Briefcase } from "lucide-react";
import { api } from "../lib/api";
import { useT } from "../i18n";

const LOGO_URL = "https://www.perfinco.pt/wp-content/uploads/2022/07/perfinco-logo.png";

export default function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { lang, setLang, t } = useT();
  const [counts, setCounts] = useState({ total: 0, urgent: 0, high: 0 });

  const isClientArea = location.pathname.startsWith("/cliente");
  const isDual = !!(user && user.role === "accountant" && user.company_id);
  const isAccountant = !!(user && user.role === "accountant" && !isClientArea);

  useEffect(() => {
    if (isAccountant && lang !== "pt") setLang("pt");
  }, [isAccountant, lang, setLang]);

  useEffect(() => {
    if (!user) return;
    const load = () => api.get("/messages/unresolved-counts").then(({ data }) => setCounts(data)).catch(() => {});
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [user]);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <header
      className="w-full border-b border-[color:var(--border)] bg-[color:var(--surface)]/90 backdrop-blur sticky top-0 z-30"
      data-testid="app-header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
        <Link to={isAccountant ? "/contabilista" : user ? "/cliente" : "/"} className="flex items-center gap-3" data-testid="brand-link">
          <img src={LOGO_URL} alt="Perfinco" className="h-7 sm:h-8 w-auto" />
          <span className="hidden sm:inline-block h-6 w-px bg-[color:var(--border)]" />
          <span className="hidden sm:inline font-display text-sm font-medium tracking-[0.18em] uppercase text-[color:var(--ink-muted)]">{t("brand.suffix")}</span>
        </Link>

        <div className="flex items-center gap-2">
          {!isAccountant && (
            <div className="hidden sm:flex items-center bg-[color:var(--paper)] border border-[color:var(--border)] rounded-full text-xs font-semibold overflow-hidden" data-testid="lang-switch">
              <button onClick={() => setLang("pt")} className={`px-2.5 py-1 ${lang === "pt" ? "bg-[color:var(--primary)] text-white" : "text-[color:var(--ink-muted)]"}`} data-testid="lang-pt">PT</button>
              <button onClick={() => setLang("en")} className={`px-2.5 py-1 ${lang === "en" ? "bg-[color:var(--primary)] text-white" : "text-[color:var(--ink-muted)]"}`} data-testid="lang-en">EN</button>
            </div>
          )}
        </div>

        {user ? (
          <div className="flex items-center gap-3">
            {isAccountant && (
              <Link to="/contabilista/historico" className="hidden sm:inline-flex items-center gap-1.5 text-sm text-[color:var(--ink-muted)] hover:text-[color:var(--ink)]" data-testid="header-history-link">
                <History size={16} /> {t("nav.history")}
              </Link>
            )}
            <Link to={isAccountant ? "/contabilista/definicoes?tab=empresas" : "/cliente/mensagens"} className="relative inline-flex items-center p-2 rounded-lg hover:bg-[color:var(--border)] transition-colors" data-testid="header-notifications-btn" title="Mensagens">
              <Bell size={18} />
              {counts.total > 0 && (
                <span className={`absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center ${counts.urgent > 0 ? "bg-[#991B1B] text-white" : counts.high > 0 ? "bg-[#F59E0B] text-white" : "bg-[color:var(--primary)] text-white"}`} data-testid="header-badge">
                  {counts.total}
                </span>
              )}
            </Link>
            {!isAccountant && (
              <Link
                to="/cliente/nova-fatura"
                className="hidden sm:inline-flex items-center gap-2 btn-primary text-sm"
                data-testid="header-new-invoice-btn"
              >
                <Camera size={16} /> {t("nav.new_invoice")}
              </Link>
            )}
            {isDual && (
              <button
                onClick={() => navigate(isClientArea ? "/contabilista" : "/cliente")}
                className="hidden sm:inline-flex items-center gap-1.5 text-sm rounded-full border border-[color:var(--primary)] text-[color:var(--primary)] px-3 py-1.5 hover:bg-[color:var(--primary)] hover:text-white transition-colors"
                data-testid="mode-toggle-btn"
              >
                {isClientArea ? <><Briefcase size={15} /> Voltar à gestão</> : <><UserCog size={15} /> Ver como cliente</>}
              </button>
            )}
            <div className="hidden sm:flex flex-col items-end leading-tight">
              <span className="text-sm font-medium" data-testid="header-user-name">{user.name}</span>
              <span className="label-eyebrow text-[10px]" data-testid="header-user-role">{isAccountant ? t("role.accountant") : t("role.client")}</span>
            </div>
            <button
              onClick={handleLogout}
              className="p-2 rounded-lg hover:bg-[color:var(--border)] transition-colors"
              title="Sair"
              data-testid="logout-btn"
            >
              <LogOut size={18} />
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <Link to="/login" className="btn-primary text-sm" data-testid="header-login-btn">{t("nav.login")}</Link>
          </div>
        )}
      </div>
    </header>
  );
}
